import { reactive, watch, ref, computed } from 'vue'
import { useFetch } from '@/composables'
import { storeToRefs, useAccountStore } from '@/store'
import dayjs, { type Dayjs } from 'dayjs'
import type { ClosedPosition } from '../types'
import utc from 'dayjs/plugin/utc'
dayjs.extend(utc)

export type SortOrder = 'asc' | 'desc'
interface OrderBy {
  field: string | undefined
  order: SortOrder | undefined
}

interface Params {
  dateRange: (string | Dayjs)[]
  page: number
  page_size: number
  symbol: string
  orderBy: OrderBy
  serverName: string | undefined
  loginId: string | undefined
}

interface FetchDataResponse {
  current_page: number
  data: ClosedPosition[]
  per_page: number
  total: number
}

const initialParams: Params = {
  dateRange: [],
  page: 1,
  page_size: 20,
  symbol: '',
  orderBy: {
    field: undefined,
    order: undefined,
  },
  serverName: undefined,
  loginId: undefined,
}

export function useClosePosition() {
  const hasMore = ref(false)
  const dataSource = ref<ClosedPosition[]>([])
  const refreshKey = ref(0)

  const accountStore = useAccountStore()
  const { selectedAccount } = storeToRefs(accountStore)
  const { loading, fetchData } = useFetch<FetchDataResponse>({
    url: '/api/trade/order_close',
    autoFetch: false,
  })

  const params = reactive<Params>({
    ...initialParams,
    dateRange: [...initialParams.dateRange],
  })

  watch(
    () => selectedAccount.value?.loginid,
    () => {
      params.loginId = selectedAccount.value?.loginid
      params.serverName = selectedAccount.value?.server_name
    },
    { immediate: true },
  )

  const queryKey = computed(() => {
    const { symbol, orderBy, serverName, loginId, dateRange } = params
    const [start, end] = (dateRange || []).map((d) => dayjs.utc(d).unix())
    return JSON.stringify({
      symbol: symbol || '',
      orderField: orderBy.field || '',
      order: orderBy.order || '',
      serverName: serverName || '',
      loginId: loginId || '',
      start: start || 0,
      end: end || 0,
      rk: refreshKey.value,
    })
  })

  watch(
    () => [queryKey.value, params.page] as const,
    async ([newKey, newPage], oldValue, onInvalidate) => {
      const [oldKey, oldPage] = oldValue || [undefined, undefined]
      if (newKey !== oldKey && newPage !== 1) {
        params.page = 1
        return
      }

      const append = newKey === oldKey && newPage !== oldPage

      let cancelled = false
      onInvalidate(() => {
        cancelled = true
      })

      if (!append) {
        dataSource.value = []
        hasMore.value = false
      }

      const { server_name, loginid } = selectedAccount.value ?? {}
      if (!server_name || !loginid) return

      const [start, end] = (params.dateRange || []).map((d) => dayjs.utc(d).unix())
      const query: Record<string, unknown> = {
        start,
        end,
        page: params.page,
        page_size: params.page_size,
        exchange_name: server_name,
        loginid,
        symbol: params.symbol,
      }
      if (params.orderBy.field && params.orderBy.order) {
        query.order_by = { [params.orderBy.field]: params.orderBy.order }
      }

      const { current_page, data, per_page, total } = await fetchData(query)

      // 若本次已被判定为过期，则不落地结果
      if (cancelled) return

      dataSource.value = append ? [...dataSource.value, ...data] : data
      hasMore.value = current_page < Math.ceil(total / per_page)
    },
    { immediate: true, flush: 'post' },
  )

  const loadMore = () => {
    if (hasMore.value && !loading.value) {
      params.page += 1
    }
  }

  const updateParams = (partial: Partial<Params>) => {
    let changed = false
    for (const k in partial) {
      if (partial[k] !== params[k]) changed = true
    }
    if (!changed) return
    Object.assign(params, partial)
  }

  /**
   * 刷新数据 - 保持当前筛选条件，重新请求第一页
   */
  const refresh = () => {
    dataSource.value = []
    hasMore.value = false
    params.page = 1
    refreshKey.value++
  }

  /**
   * 重置为默认参数并重新请求数据
   */
  const reset = () => {
    Object.assign(params, {
      ...initialParams,
      dateRange: [...initialParams.dateRange],
      // 保留账户信息
      serverName: selectedAccount.value?.server_name,
      loginId: selectedAccount.value?.loginid,
    })
    params.page = 1
    refreshKey.value++
  }

  return {
    hasMore,
    params,
    loading,
    updateParams,
    dataSource,
    loadMore,
    refresh,
    reset,
  }
}
